import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import type { FlightsClassTypes } from 'constants/flight'
import { fareClassToApiCabinMap } from 'constants/flight'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useContext } from 'react'
import type { FareFamiliesResponse } from 'api/flights'
import { getFlightFareFamilies } from 'api/flights'
import GeoContext from 'contexts/geoContext'
import { countOccupantsForFlights, mergeOccupants } from 'lib/offer/occupancyUtils'
import { useQuery } from '@tanstack/react-query'
import { EmptyObject } from 'lib/object/objectUtils'
import useAllCharterFlightJourneys from './useAllCharterFlightJourneys'
import moment from 'moment'
import { useAppSelector } from 'hooks/reduxHooks'

export function extractDepartingAndReturningFaresForClass(fareFamilies: FareFamiliesResponse, fareClass: FlightsClassTypes): [App.FlightFareFamily | undefined, App.FlightFareFamily | undefined] {
  const apiCabinName = fareClassToApiCabinMap[fareClass]
  const departingFareFamily = fareFamilies.upsellFaresPerLeg?.[0]?.find(upsell => upsell.cabin === apiCabinName)
  const returningFareFamily = fareFamilies.upsellFaresPerLeg?.[1]?.find(upsell => upsell.cabin === apiCabinName)
  return [departingFareFamily, returningFareFamily]
}

export default function useCharterFlightJourneysForHotelDates({
  charterBundleRoute,
  checkInDate,
  checkOutDate,
  occupancies = EmptyArray,
  fareClass,
  disabled,
  feederOriginAirportCode,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FlightsClassTypes
  disabled?: boolean
  feederOriginAirportCode?: string
}) {
  const allowIncompleteJourneys = useFeatureFlag(OptimizelyFeatureFlags.flightsIncompleteCharterBundles)
  const { currentRegionCode } = useContext(GeoContext)
  const email = useAppSelector(state => state.auth.account.email)

  // GET FLIGHTS
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass,
    occupancies,
    disabled,
    feederOriginAirportCode,
  })

  const providerSearchId = flightResults?.searchId
  const searchResultsTimestamp = flightResults?.timestamp
  const journeysError = flightResults?.error
  const fetchingJourneys = flightResults?.fetching

  const departingJourney = checkInDate
    ? flightResults?.fares.at(0)?.find(journey => {
        const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
        return arrivalDate && moment(arrivalDate).format('YYYY-MM-DD') === checkInDate
      })
    : flightResults?.fares.at(0)?.[0]

  // Constrain returning fares to those paired with the chosen departure via
  // `pairedFareId` before matching on date. Same-date returns can be unpaired, so
  // date-only matching can select a return the user could never have picked in the
  // calendar — building checkout with an invalid leg combination. Falls back to all
  // returns when pairing data is absent. Mirrors `matchCharterJourneys` (Trip Chat
  // hydration) and the calendar's `useCharterDateRangesByMonth`.
  const returningFares = flightResults?.fares.at(1)
  const pairedFareIds = departingJourney?.pairedFareId
  const pairedReturns = pairedFareIds?.length
    ? returningFares?.filter(journey => pairedFareIds.includes(journey.id))
    : returningFares

  const returningJourney = checkOutDate
    ? pairedReturns?.find(journey => {
        const departingDate = journey.flightGroup.flights.at(0)?.departingDate
        return departingDate && moment(departingDate).format('YYYY-MM-DD') === checkOutDate
      })
    : pairedReturns?.[0]

  // GET FARE FAMILIES
  const {
    data: { fareFamilies, departingFareFamily, returningFareFamily } = EmptyObject as { fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined },
    status: fareFamiliesFetchStatus,
  } = useQuery<{ fareFamilies?: FareFamiliesResponse, departingFareFamily?: App.FlightFareFamily | undefined, returningFareFamily?: App.FlightFareFamily | undefined }>({
    queryFn: async() => {
      if (!providerSearchId || (!departingJourney && !returningJourney)) {
        return EmptyObject
      }

      // Important: Fare-family pricing depends on passenger types (ADT/CHD/INF).
      // Charter/hotel offers can treat age 2 as an "infant" for accommodation, but for flights
      // age 2 must be a "child". So always map occupancies into flight context first.
      const flightOccupancies = countOccupantsForFlights(occupancies)
      const fareFamilies = await getFlightFareFamilies({
        searchId: providerSearchId,
        sessionId: '',
        origin: (departingJourney ?? returningJourney)!.flightGroup.flights[0].departureAirport,
        destination: (departingJourney ?? returningJourney)!.flightGroup.flights.at(-1)!.arrivalAirport,
        occupancies: mergeOccupants([flightOccupancies]),
        region: currentRegionCode,
        isStandalone: false,
        selectedFareIds: [departingJourney, returningJourney].flatMap(journey => journey ? [journey.id] : []),
        feederOriginAirportCode,
        email,
      })

      const singleFareFamily = fareFamilies.upsellFares.find(fare => fare.cabin === fareClassToApiCabinMap[fareClass])
      const [departingFareFamily, returningFareFamily] = departingJourney && returningJourney
        ? extractDepartingAndReturningFaresForClass(fareFamilies, fareClass)
        : [departingJourney ? singleFareFamily : undefined, returningJourney ? singleFareFamily : undefined]

      return { fareFamilies, departingFareFamily, returningFareFamily }
    },
    queryKey: [providerSearchId, fareClass, departingJourney?.flightGroup.flights[0].departureAirport, returningJourney?.flightGroup.flights[0].departureAirport, departingJourney?.id, returningJourney?.id, occupancies, currentRegionCode, email, feederOriginAirportCode],
    enabled: !!(providerSearchId && (departingJourney || returningJourney)),
  })

  // If journey search is complete but no journeys found, we should not be fetching
  // However, if we have route and dates but no data yet, we should show loading (especially for SSR)
  const hasSearchCompleted = !!searchResultsTimestamp || !!journeysError
  const shouldBeFetching = !!charterBundleRoute && (
    fetchingJourneys ||
    (fareFamiliesFetchStatus === 'loading' && !!(departingJourney || returningJourney)) ||
    (!hasSearchCompleted && !departingJourney && !returningJourney && (!!checkInDate || !!checkOutDate))
  )
  const hasAllFareData = (allowIncompleteJourneys ? !!(departingJourney || returningJourney) : !!(departingJourney && returningJourney)) &&
    (!departingJourney || !!departingFareFamily) && (!returningJourney || !!returningFareFamily)

  return {
    departingJourney,
    returningJourney,
    fetching: shouldBeFetching,
    error: !!charterBundleRoute && (!!journeysError || fareFamiliesFetchStatus === 'error'),
    searchResultsTimestamp,
    providerSearchId,
    departingFareFamily,
    returningFareFamily,
    fareFamilies,
    metadata: flightResults?.metadata,
    hasAllFareData,
  }
}
